import React, { useState, useCallback, useEffect, useRef } from 'react';
import './App.css';
import PRODUCT_DB_MAP from './config/productDatabaseMap';
import { PRODUCT_OPTIONS, SECTOR_ORDER, PRODUCT_NAMES } from './config/productCatalog';
import Sidebar from './components/Sidebar';
import ClientPortalSidebar from './components/ClientPortalSidebar';
import ClientOnboarding from './components/ClientOnboarding';
import ClientDashboard from './components/ClientDashboard';
import AgenticAI from './components/AgenticAI';
import PermissionLogs from './components/PermissionLogs';
import GenAIModule from './components/GenAIModule';
import ListClients from './components/ListClients';
import UpdateClient from './components/UpdateClient';
import RemoveClient from './components/RemoveClient';
import ViewClient from './components/ViewClient';
import ClientApplication from './components/ClientApplication';
import LaunchClientApp from './components/LaunchClientApp';
import ViewConfiguration from './components/ViewConfiguration';
import CognitoCallback from './components/CognitoCallback';
import ThingsToDo from './components/ThingsToDo';
import AccountPage from './components/AccountPage';
import AdminDashboard from './components/AdminDashboard';

/**
 * URL Branch Routing:
 *   /distributors  → locked to a specific distributor client (Prab-Restaurant)
 *   /builders      → locked to a specific builder client (Prabs-National-Bank)
 *   /pnp_main      → full admin view with all clients (same as localhost:3000)
 *   /callback      → Cognito OAuth handler
 *   /signup        → Self-service subscriber signup
 *   /subscriber    → Subscriber-isolated portal (single client, locked product type)
 *   /              → default (same as /pnp_main)
 *
 * Build: 2026-04-22-v2 — subscriber lockdown + auto-lookup
 */

// Pre-configured distributor clients (locked — no client switching)
const DISTRIBUTOR_CLIENTS = {
  distributors: {
    client_id: 'client_prab_001',
    company_name: 'Prab-Restaurant-Hospitality',
    product_name: 'Restaurant Intelligence',
    product_type: 'Restaurant.Type-1',
    tenant_type: 'INDEPENDENT',
    plan: 'starter',
  },
  builders: {
    client_id: 'client_pnb_fin_001',
    company_name: 'Prabs-National-Bank',
    product_name: 'BankRisk-LLM',
    product_type: 'Financial-Bank-Type1',
    tenant_type: 'INDEPENDENT',
    plan: 'enterprise',
  },
};

/**
 * Subscriber session helpers.
 * A subscriber session is stored in localStorage after signup/login.
 * Shape: { company_name, email, product_type, client_id, plan, onboarded }
 */
export function getSubscriberSession() {
  try {
    const raw = localStorage.getItem('pnp_subscriber_session');
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function setSubscriberSession(data) {
  localStorage.setItem('pnp_subscriber_session', JSON.stringify(data));
  // Also persist into the multi-client session store so ListClients can repair all clients
  try {
    const all = JSON.parse(localStorage.getItem('pnp_all_sessions') || '[]');
    const idx = all.findIndex(s => s.client_id === data.client_id);
    if (idx >= 0) all[idx] = data;
    else all.push(data);
    localStorage.setItem('pnp_all_sessions', JSON.stringify(all));
  } catch { /* ignore */ }
}

export function clearSubscriberSession() {
  localStorage.removeItem('pnp_subscriber_session');
}

// Detect which branch we're on from the URL path
function getUrlBranch() {
  const path = window.location.pathname.toLowerCase().replace(/^\/+|\/+$/g, '');
  if (path === 'callback') return 'callback';
  if (path === 'distributors') return 'distributors';
  if (path === 'builders') return 'builders';
  if (path === 'signup') return 'signup';
  if (path === 'subscriber') return 'subscriber';
  if (path === 'pnp_main' || path === 'main') return 'pnp_main';
  if (path === 'pnp_staging') return 'pnp_staging';
  return 'pnp_main'; // default = full admin
}

// Build version marker — forces cache bust on deploy
const APP_BUILD_VERSION = '2026-04-22-v5-hotfix';

function App() {
  const urlBranch = getUrlBranch();

  // ── Cognito Callback Route ──
  if (urlBranch === 'callback') {
    return <CognitoCallback onLoginSuccess={() => { window.location.href = '/pnp_main'; }} />;
  }

  // ── Signup Route (self-service, no auth required) ──
  // If user already has a subscriber session WITH a business, redirect to /subscriber
  // If they only have an email (no business yet), let them see signup
  if (urlBranch === 'signup') {
    const existingSession = getSubscriberSession();
    if (existingSession && existingSession.email && existingSession.client_id && existingSession.onboarded) {
      window.location.href = '/subscriber';
      return null;
    }
    return <SignupPage />;
  }

  // ── Subscriber Route (isolated single-client portal) ──
  if (urlBranch === 'subscriber') {
    // ── Check for ?verified=true query param (post-verification redirect) ──
    const subscriberParams = new URLSearchParams(window.location.search);
    const justVerified = subscriberParams.get('verified') === 'true';

    // ── If arriving from email verification, build session from query params ──
    if (justVerified) {
      const verifiedEmail = subscriberParams.get('email') || '';
      const verifiedBusinessName = subscriberParams.get('business_name') || '';
      const verifiedProductType = subscriberParams.get('product_type') || '';
      const verifiedProductName = subscriberParams.get('product_name') || '';

      // Build or update subscriber session with verified context.
      // Always refresh product_type, product_name, company_name and reset
      // onboarded/client_id — a new signup for a different product on the
      // same email must not inherit the previous product's session state.
      let session = getSubscriberSession();
      if (verifiedEmail || verifiedProductType) {
        session = {
          company_name: verifiedBusinessName || (session && session.company_name) || '',
          email: verifiedEmail || (session && session.email) || '',
          location: (session && session.location) || '',
          product_type: verifiedProductType || (session && session.product_type) || '',
          product_name: verifiedProductName || verifiedProductType || (session && session.product_name) || '',
          client_id: '',          // reset — new signup, not yet provisioned
          plan: 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'INDEPENDENT',
          onboarded: false,       // reset — must go through onboarding wizard
          payment_validated: false,
          trial_start: null,
          created_at: new Date().toISOString(),
        };
        setSubscriberSession(session);
      } else if (!session) {
        session = {
          company_name: '',
          email: '',
          location: '',
          product_type: '',
          product_name: '',
          client_id: '',
          plan: 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'INDEPENDENT',
          onboarded: false,
          payment_validated: false,
          trial_start: null,
          created_at: new Date().toISOString(),
        };
        setSubscriberSession(session);
      }
      // Clean URL to remove query params (keep verified=true for banner)
      window.history.replaceState({}, '', '/subscriber?verified=true');
      return <SubscriberApp session={session} verified={true} />;
    }

    const session = getSubscriberSession();
    if (!session) {
      // Check if user just signed in (has token + email but no subscriber session)
      const userEmail = localStorage.getItem('pnp_user_email');
      if (userEmail) {
        // Create a minimal subscriber session from the signin
        const autoSession = {
          company_name: '',
          email: userEmail,
          location: '',
          product_type: '',
          product_name: '',
          client_id: '',
          plan: 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'INDEPENDENT',
          onboarded: false,
          payment_validated: false,
          trial_start: null,
          created_at: new Date().toISOString(),
        };
        setSubscriberSession(autoSession);
        return <SubscriberApp session={autoSession} verified={justVerified} />;
      }
      window.location.href = '/signup';
      return null;
    }
    return <SubscriberApp session={session} verified={justVerified} />;
  }

  // ── Auth Guard: require login for all routes except callback/signup ──
  // Skip auth on localhost for development
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const token = localStorage.getItem('pnp_access_token');
  if (!token && !isLocalhost) {
    // Store current path so login can redirect back (staging vs prod)
    const currentPath = window.location.pathname;
    if (currentPath && currentPath !== '/' && currentPath !== '/login') {
      localStorage.setItem('pnp_post_login_path', currentPath);
    }
    window.location.href = '/login';
    return null;
  }

  // ── Role-based routing: non-admin users go to /subscriber ──
  // Only @empirical-ais.com emails get access to /pnp_main (admin console)
  // All other authenticated users are redirected to /subscriber
  if (urlBranch === 'pnp_main' || urlBranch === 'pnp_staging') {
    const userEmail = localStorage.getItem('pnp_user_email') || '';
    const subscriberSession = getSubscriberSession();
    const effectiveEmail = userEmail || (subscriberSession && subscriberSession.email) || '';
    const isAdmin = effectiveEmail.toLowerCase().endsWith('@empirical-ais.com') || isLocalhost;
    if (!isAdmin && effectiveEmail) {
      // Non-admin user — redirect to subscriber portal
      if (!subscriberSession) {
        setSubscriberSession({
          company_name: '',
          email: effectiveEmail,
          location: '',
          product_type: '',
          product_name: '',
          client_id: '',
          plan: 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'INDEPENDENT',
          onboarded: false,
          payment_validated: false,
          trial_start: null,
          created_at: new Date().toISOString(),
        });
      }
      window.location.href = '/subscriber';
      return null;
    }
  }

  // ── Distributor / Builder Routes (locked to single client) ──
  if (urlBranch === 'distributors' || urlBranch === 'builders') {
    return <LockedClientPortal branch={urlBranch} />;
  }

  // ── PNP Main / Admin Route (full access, all clients) ──
  // Both pnp_main and pnp_staging use the same app — staging just shows a banner
  return <PNPMainApp isStaging={urlBranch === 'pnp_staging'} />;
}

/**
 * LockedClientPortal — Distributor/Builder view locked to a single client.
 * No PNP operator menus. No client switching. Just the client application.
 */
function LockedClientPortal({ branch }) {
  const clientConfig = DISTRIBUTOR_CLIENTS[branch];
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="app client-portal-layout">
      <ClientPortalSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        clientConfig={clientConfig}
        onBackToOperator={() => { window.location.href = '/pnp_main'; }}
      />
      <main className="main-content">
        <header className="header">
          <h1>PNP-Application — {clientConfig.company_name}</h1>
          <p className="subtitle">
            {branch === 'distributors' ? 'Distributor Portal' : 'Builder Portal'} • {clientConfig.tenant_type} • {clientConfig.plan}
          </p>
        </header>
        <ClientApplication
          clientConfig={clientConfig}
          onBack={() => { window.location.href = '/pnp_main'; }}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      </main>
    </div>
  );
}

/**
 * PNPMainApp — Full admin view with all clients (same as localhost:3000).
 * Onboard, List, View, Update, Remove, Launch — everything.
 */
function PNPMainApp({ isStaging = false }) {

  // Log build version for debugging cache issues
  React.useEffect(() => { console.log('[PNP] Build:', APP_BUILD_VERSION); }, []);

  // Operator Mode: sidebar locked to onboarding until setup is complete
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [activeModule, setActiveModule] = useState('onboard');
  const [clientConfig, setClientConfig] = useState(null);

  // Client Portal state — the "Two-World" split
  // When launchedApp is set, the entire UI switches to the Client Portal layout
  // PNP-Main-Menus are physically absent from the DOM
  const [launchedApp, setLaunchedApp] = useState(null);
  const [portalActiveTab, setPortalActiveTab] = useState('overview');

  // ── @empirical-ais.com Auto-Routing to Admin Dashboard ──
  // When an admin user signs in, auto-land them on the Admin Dashboard
  // with the Data Mesh P-LOB/I-LOB view
  useEffect(() => {
    const userEmail = localStorage.getItem('pnp_user_email') || '';
    const isAdmin = userEmail.toLowerCase().endsWith('@empirical-ais.com');
    if (isAdmin && activeModule === 'onboard') {
      // Auto-route admin users to the Admin Dashboard on initial load
      setActiveModule('admin-dashboard');
      console.log('[PNP] Admin user detected, auto-routing to Admin Dashboard');
    }
  }, []); // Run once on mount

  const handleOnboardingComplete = useCallback((config) => {
    setClientConfig(config);
    setIsOnboarded(true);
    setActiveModule('dashboard');
    // Persist to localStorage so ListClients can show it even without backend
    try {
      const existing = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]');
      const already = existing.find(c => c.client_id === config.client_id);
      if (!already) {
        const entry = {
          client_id: config.client_id,
          company_name: config.company_name,
          product_name: config.product_name || '',
          product_type: config.product_type || '',
          tenant_type: config.tenant_type || 'INDEPENDENT',
          plan: config.plan || 'starter',
          status: 'active',
          location: config.location || '',
          app_model: config.franchise_or_retail || 'Standalone',
          created_at: new Date().toISOString(),
        };
        localStorage.setItem('pnp_onboarded_clients', JSON.stringify([entry, ...existing]));
      }
      // ── Set Product-Type BUC defaults on onboarding/update-client ──
      // This ensures BUC/BUC-Questions are set once during onboarding
      const pt = (config.product_type || '').toLowerCase();
      const ver = `v3_${pt.split('.')[0] || 'generic'}`;
      localStorage.setItem(`pnp_daily_ver_${config.client_id}`, ver);
      // Clear any stale BUC button labels so product-type defaults take effect
      ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_daily_${k}_${config.client_id}`));
      ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_analytics_${k}_${config.client_id}`));
    } catch { /* ignore storage errors */ }
  }, []);

  const handleLaunchApp = useCallback((config) => {
    setLaunchedApp(config);
    setPortalActiveTab('overview');
    // Also persist launched clients to localStorage for ListClients
    try {
      const existing = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]');
      const already = existing.find(c => c.client_id === config.client_id);
      if (!already) {
        const entry = {
          client_id: config.client_id,
          company_name: config.company_name,
          product_name: config.product_name || '',
          product_type: config.product_type || '',
          tenant_type: config.tenant_type || 'INDEPENDENT',
          plan: config.plan || 'starter',
          status: 'active',
          location: config.location || '',
          app_model: config.app_model || 'Standalone',
          created_at: config.created_at || new Date().toISOString(),
        };
        localStorage.setItem('pnp_onboarded_clients', JSON.stringify([entry, ...existing]));
      }
    } catch { /* ignore */ }
  }, []);

  const handleBackFromPortal = useCallback(() => {
    setLaunchedApp(null);
    setPortalActiveTab('overview');
  }, []);

  const handleSetActiveModule = useCallback((mod) => {
    // Clear launched app when navigating via sidebar
    setLaunchedApp(null);
    // Allow client management menus even before onboarding
    const alwaysAllowed = ['onboard', 'admin-dashboard', 'list-clients', 'view-client', 'update-client', 'remove-client', 'launch-app', 'things-to-do'];
    if (!isOnboarded && !alwaysAllowed.includes(mod)) return;
    setActiveModule(mod);
  }, [isOnboarded]);

  // ── Client Portal Renderer (The "Launch" View) ──
  // Route: /portal/:clientId — PNP-Main-Menus are physically absent
  const renderPortalModule = () => {
    // The ClientApplication component handles all portal tabs internally
    return (
      <ClientApplication
        clientConfig={launchedApp}
        onBack={handleBackFromPortal}
        activeTab={portalActiveTab}
        setActiveTab={setPortalActiveTab}
      />
    );
  };

  // ── PNP Operator Renderer (The Management Dashboard) ──
  const renderOperatorModule = () => {
    // Client management screens — available before and after onboarding
    switch (activeModule) {
      case 'admin-dashboard':
        return <AdminDashboard />;
      case 'list-clients':
        return <ListClients clientConfig={clientConfig} onLaunchApp={handleLaunchApp} />;
      case 'view-client':
        return <ViewClient clientConfig={clientConfig} onLaunchApp={handleLaunchApp} />;
      case 'update-client':
        return <UpdateClient clientConfig={clientConfig} onComplete={() => setActiveModule('view-client')} onLaunchApp={handleLaunchApp} />;
      case 'remove-client':
        return <RemoveClient clientConfig={clientConfig} />;
      case 'launch-app':
        return <LaunchClientApp onLaunchApp={handleLaunchApp} />;
      case 'things-to-do':
        return <ThingsToDo />;
      default:
        break;
    }

    if (!isOnboarded || activeModule === 'onboard') {
      return <ClientOnboarding onComplete={handleOnboardingComplete} onLaunchApp={handleLaunchApp} />;
    }

    switch (activeModule) {
      case 'dashboard':
        return <ClientDashboard clientConfig={clientConfig} />;
      case 'agentic':
        return <AgenticAI clientConfig={clientConfig} />;
      case 'permissions':
        return <PermissionLogs clientConfig={clientConfig} />;
      case 'askme':
        return <GenAIModule clientConfig={clientConfig} mode="askme" />;
      case 'command':
        return <GenAIModule clientConfig={clientConfig} mode="command" />;
      default:
        return <GenAIModule clientConfig={clientConfig} mode={activeModule} />;
    }
  };

  // ── Two-World Layout Decision ──
  // If launchedApp is set → Client Portal (no PNP menus in DOM)
  // Otherwise → PNP Operator Console
  if (launchedApp) {
    return (
      <div className="app client-portal-layout">
        <ClientPortalSidebar
          activeTab={portalActiveTab}
          setActiveTab={setPortalActiveTab}
          clientConfig={launchedApp}
          onBackToOperator={handleBackFromPortal}
        />
        <main className="main-content">
          <header className="header">
            <h1>PNP-Application — {launchedApp.company_name}, {launchedApp.client_id}</h1>
            <p className="subtitle">
              Live Application • {(launchedApp.tenant_type || 'standalone').toUpperCase()} • {launchedApp.plan || 'starter'}
            </p>
          </header>
          {renderPortalModule()}
        </main>
      </div>
    );
  }

  // PNP Operator Console
  const subtitle = isOnboarded && clientConfig
    ? `${clientConfig.company_name} — Operations Dashboard`
    : `Operator Mode — Complete onboarding to unlock`;

  return (
    <div className="app">
      <Sidebar
        activeModule={activeModule}
        setActiveModule={handleSetActiveModule}
        clientConfig={clientConfig}
        isOnboarded={isOnboarded}
      />
      <main className="main-content">
        <header className="header">
          <h1>Plug-N-Play-Infra.ai: Business Intelligence Platform</h1>
          {isStaging && (
            <div style={{ display: 'inline-block', background: '#f39c12', color: '#2c3e50', padding: '2px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 700, marginBottom: '4px', letterSpacing: '0.5px' }}>
              ⚠️ STAGING ENVIRONMENT
            </div>
          )}
          <p className="subtitle">{subtitle}</p>
        </header>
        {renderOperatorModule()}
      </main>
    </div>
  );
}

/**
 * SignupConfirmation — Shown after successful signup submission.
 * Displays "Check Your Email" screen with resend and change-email options.
 */
function SignupConfirmation({ email, maskedEmail, onResend, onChangeEmail }) {
  const [cooldown, setCooldown] = useState(0);
  const [resendStatus, setResendStatus] = useState('');

  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  const handleResend = async () => {
    setResendStatus('sending');
    try {
      const res = await fetch('/v1/subscribers/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.status === 'cooldown') {
        setCooldown(data.cooldown_remaining || 60);
        setResendStatus('');
      } else if (data.status === 'ok') {
        setCooldown(60);
        setResendStatus('sent');
      } else {
        setResendStatus('error');
      }
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div style={{ textAlign: 'center', padding: '40px 20px', maxWidth: '500px', margin: '0 auto' }}>
      <div style={{ fontSize: '64px', marginBottom: '20px' }}>📬</div>
      <h2 style={{ color: '#302b63', marginBottom: '12px' }}>Check Your Email</h2>
      <p style={{ color: '#555', fontSize: '16px', marginBottom: '8px' }}>
        We sent a verification link to <strong>{maskedEmail}</strong>
      </p>
      <p style={{ color: '#888', fontSize: '14px', marginBottom: '24px' }}>
        Click the link in the email to verify your account. Check your spam/junk folder if you don't see it.
      </p>
      {resendStatus === 'sent' && (
        <p style={{ color: '#4CAF50', fontSize: '14px', marginBottom: '12px' }}>Verification email resent!</p>
      )}
      {resendStatus === 'error' && (
        <p style={{ color: '#f44336', fontSize: '14px', marginBottom: '12px' }}>Failed to resend. Please try again.</p>
      )}
      <button
        onClick={handleResend}
        disabled={cooldown > 0 || resendStatus === 'sending'}
        style={{
          padding: '10px 24px', background: cooldown > 0 ? '#ccc' : '#302b63',
          color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px',
          cursor: cooldown > 0 ? 'default' : 'pointer', marginBottom: '16px',
        }}
      >
        {resendStatus === 'sending' ? 'Sending...' : cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend Verification Email'}
      </button>
      <br />
      <a href="#" onClick={(e) => { e.preventDefault(); onChangeEmail(); }}
        style={{ color: '#1565C0', fontSize: '14px' }}>
        Use a different email address
      </a>
    </div>
  );
}

/**
 * SignupPage — Self-service signup for subscribers.
 * Collects company name, email, and locks product_type to what they selected.
 * After signup, creates a subscriber session and redirects to /subscriber.
 * Now supports email verification flow: on signup, sends verification email
 * and shows SignupConfirmation screen instead of redirecting immediately.
 */
function SignupPage() {
  const [companyName, setCompanyName] = useState('');
  const [location, setLocation] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [productType, setProductType] = useState('Restaurant.Type-1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // Existing businesses state (duplicate email gate)
  const [existingBusinesses, setExistingBusinesses] = useState(null);
  const [showExistingModal, setShowExistingModal] = useState(false);

  // ── Email verification flow state ──────────────────────────────────────
  const [signupPhase, setSignupPhase] = useState('form');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');

  // ── Read URL query params on mount for contextual banners ──────────────
  const [urlBanner, setUrlBanner] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const errorParam = params.get('error');
    const messageParam = params.get('message');
    const emailParam = params.get('email');

    if (errorParam === 'token_expired') {
      setUrlBanner({ type: 'warning', text: 'Your verification link has expired. Please sign up again or request a new link.' });
    } else if (messageParam === 'already_verified') {
      setUrlBanner({ type: 'info', text: 'Your email is already verified. Please sign in.' });
    }
    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, []);

  // ── PRODUCT TYPES — imported from shared catalog ──────────────────────────
  // Single source of truth: src/config/productCatalog.js
  // Add new products there → both Signup and Subscriber Onboarding update automatically.

  // ── Search/filter state for product type selector ──────────────────────
  const [productSearch, setProductSearch] = useState('');

  // Filter products by search term (matches label, sector, subSector)
  const filteredProducts = PRODUCT_OPTIONS.filter(p => {
    if (!productSearch.trim()) return true;
    const q = productSearch.toLowerCase();
    return p.label.toLowerCase().includes(q) ||
           p.sector.toLowerCase().includes(q) ||
           p.subSector.toLowerCase().includes(q) ||
           (p.emoji && p.emoji.includes(q));
  });

  const productNames = PRODUCT_NAMES;

  // Handle selecting an existing business from the modal → load session and go to /subscriber
  const handleSelectExistingBiz = (biz) => {
    const session = {
      company_name: biz.company_name,
      email: email,
      location: biz.location || biz.business_address || '',
      business_address: biz.business_address || biz.location || '',
      website_url: biz.website_url || biz.website || '',
      product_type: biz.product_type,
      product_name: biz.product_name || biz.product_type,
      client_id: biz.client_id,
      plan: biz.plan || 'growth',
      tenant_role: 'subscriber',
      tenant_type: 'INDEPENDENT',
      onboarded: true,
      payment_validated: true,
      trial_start: biz.created_at || new Date().toISOString(),
      created_at: biz.created_at || new Date().toISOString(),
    };
    setSubscriberSession(session);
    setShowExistingModal(false);
    window.location.href = '/subscriber';
  };

  // Handle "Create New Business" from existing businesses modal → bypass duplicate check
  const handleCreateNewBizFromModal = async () => {
    setShowExistingModal(false);
    setExistingBusinesses(null);
    setSubmitting(true);
    setError('');
    try {
      const API = process.env.REACT_APP_API_BASE || (window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1');
      const res = await fetch(`${API}/clients/onboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          contact_email: email,
          location: location || '',
          product_type: productType,
          product_name: productNames[productType] || productType,
          plan: 'growth',
          tenant_role: 'subscriber',
          tenant_type: 'standalone',
          franchise_or_retail_subscription: 'retail_subscription',
          skip_duplicate_check: true,
        }),
      });
      let clientId;
      if (res.ok) {
        const data = await res.json();
        clientId = data.client_id;
      }
      if (!clientId) {
        const slug = companyName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        clientId = `client_auto_${slug}`;
      }
      const session = {
        company_name: companyName,
        email: email,
        location: location || '',
        product_type: productType,
        product_name: productNames[productType] || productType,
        client_id: clientId,
        plan: 'growth',
        tenant_role: 'subscriber',
        tenant_type: 'INDEPENDENT',
        onboarded: false,
        payment_validated: false,
        trial_start: null,
        created_at: new Date().toISOString(),
      };
      setSubscriberSession(session);
      window.location.href = '/subscriber';
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Password validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      setSubmitting(false);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setSubmitting(false);
      return;
    }

    try {
      const API = process.env.REACT_APP_API_BASE || (window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1');

      // ── Call the new email verification signup endpoint ──────────────
      const res = await fetch(`${API}/subscribers/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          password: password,
          business_name: companyName,
          business_address: location || '',
          product_type: productType,
          product_name: productNames[productType] || productType,
          signup_origin: window.location.origin,
        }),
      });

      const data = await res.json();

      // ── Handle verification_sent → show confirmation screen ─────────
      if (data.status === 'verification_sent') {
        setVerificationEmail(data.email || email);
        setMaskedEmail(data.masked_email || email);
        setSignupPhase('confirmation');
        setSubmitting(false);
        return;
      }

      // ── Handle SES sandbox error → show orange banner ───────────────
      if (data.status === 'ses_error') {
        setError('');
        setUrlBanner({ type: 'ses_error', text: data.message || 'Email verification is limited to pre-approved addresses during beta. Please contact support.' });
        setSubmitting(false);
        return;
      }

      // ── Handle rate limiting → show inline error ────────────────────
      if (data.status === 'rate_limited') {
        setError(data.message || 'Too many signup attempts. Please wait before trying again.');
        setSubmitting(false);
        return;
      }

      // ── Handle existing account → show sign-in link ─────────────────
      if (data.status === 'existing_account') {
        setError('');
        setUrlBanner({ type: 'existing', text: data.message || 'This email is already registered.' });
        setSubmitting(false);
        return;
      }

      // ── Handle existing_email (legacy duplicate gate) ───────────────
      if (data.status === 'existing_email' && data.businesses?.length > 0) {
        setExistingBusinesses(data.businesses);
        setShowExistingModal(true);
        setSubmitting(false);
        return;
      }

      // ── All other responses are errors ──────────────────────────────
      // The old fallback to /v1/clients/onboard has been removed.
      // The new verification flow is the sole signup path for subscribers.
      // Account provisioning happens after email verification + payment/coupon.
      setError(data.message || 'Signup failed. Please try again.');
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      {/* ── Existing Businesses Modal (duplicate email gate) ──────────────── */}
      {showExistingModal && existingBusinesses && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '36px',
            maxWidth: '560px', width: '90%', maxHeight: '80vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          }}>
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', fontSize: '20px' }}>📋 Email Already Registered</h2>
            <p style={{ color: '#666', margin: '0 0 6px', fontSize: '14px' }}>
              <strong>{email}</strong> already has {existingBusinesses.length} registered business{existingBusinesses.length > 1 ? 'es' : ''}.
            </p>
            <p style={{ color: '#1565C0', margin: '0 0 20px', fontSize: '13px', fontWeight: 600 }}>
              Sign in to an existing business or create a new one.
            </p>

            {existingBusinesses.map((biz, idx) => (
              <div key={biz.client_id || idx} onClick={() => handleSelectExistingBiz(biz)} style={{
                border: '1px solid #e0e0e0', borderRadius: '10px', padding: '16px',
                marginBottom: '10px', cursor: 'pointer', transition: 'all 0.2s',
                background: '#f9f9f9',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#f0f0ff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e0e0e0'; e.currentTarget.style.background = '#f9f9f9'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong style={{ fontSize: '15px', color: '#1a1a2e' }}>{biz.company_name || 'Unnamed Business'}</strong>
                    <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                      {biz.product_name || biz.product_type || 'No product'} &nbsp;·&nbsp; {biz.plan || 'No plan'} &nbsp;·&nbsp;
                      <span style={{ color: biz.status === 'active' ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>
                        {biz.status || 'unknown'}
                      </span>
                    </div>
                  </div>
                  <span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '13px' }}>Sign In →</span>
                </div>
              </div>
            ))}

            <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '12px', display: 'flex', gap: '12px' }}>
              <button onClick={handleCreateNewBizFromModal} style={{
                flex: 1, padding: '12px', borderRadius: '8px', border: '2px solid #4f46e5',
                background: '#fff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', fontSize: '14px',
              }}>
                + Create New Business
              </button>
              <button onClick={() => { setShowExistingModal(false); setExistingBusinesses(null); }} style={{
                padding: '12px 20px', borderRadius: '8px', border: '1px solid #ddd',
                background: '#f5f5f5', color: '#666', cursor: 'pointer', fontSize: '14px',
              }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img 
            src="/PNP_RAG_GENAI_Agentic_ai.jpeg" 
            alt="PNP Business Atomic Intelligence" 
            style={{ width: '70px', height: '70px', borderRadius: '12px', objectFit: 'cover', marginBottom: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}
          />
          <h1 style={{ fontSize: '24px', color: '#1a1a2e', margin: '0 0 8px' }}>🚀 Plug-N-Play-Infra.ai</h1>
          <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Sign up for Business Intelligence</p>
        </div>

        {/* ── Confirmation phase: show SignupConfirmation instead of form ── */}
        {signupPhase === 'confirmation' ? (
          <SignupConfirmation
            email={verificationEmail}
            maskedEmail={maskedEmail}
            onResend={async (em) => {
              try {
                const res = await fetch('/v1/subscribers/resend-verification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ email: em }),
                });
                return await res.json();
              } catch { return { status: 'error' }; }
            }}
            onChangeEmail={() => { setSignupPhase('form'); setUrlBanner(null); }}
          />
        ) : (
        <>
        {/* ── Contextual banners from URL params and response statuses ── */}
        {urlBanner && urlBanner.type === 'warning' && (
          <div style={{ background: '#FFF3E0', color: '#E65100', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FFB74D' }}>
            ⚠️ {urlBanner.text}
          </div>
        )}
        {urlBanner && urlBanner.type === 'info' && (
          <div style={{ background: '#E3F2FD', color: '#1565C0', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #90CAF9' }}>
            ℹ️ {urlBanner.text} <a href="/subscriber" style={{ color: '#1565C0', fontWeight: 600 }}>Sign in →</a>
          </div>
        )}
        {urlBanner && urlBanner.type === 'ses_error' && (
          <div style={{ background: '#FFF3E0', color: '#E65100', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #FFB74D' }}>
            ⚠️ {urlBanner.text}
          </div>
        )}
        {urlBanner && urlBanner.type === 'existing' && (
          <div style={{ background: '#E3F2FD', color: '#1565C0', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', border: '1px solid #90CAF9' }}>
            ℹ️ {urlBanner.text} <a href="/subscriber" style={{ color: '#1565C0', fontWeight: 600 }}>Sign in →</a>
          </div>
        )}

        {error && <div style={{ background: '#FFEBEE', color: '#C62828', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}

        <form onSubmit={handleSignup}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Company Name *</label>
            <input
              type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
              placeholder="e.g. Empirical-Test1" required
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Location *</label>
            <input
              type="text" value={location} onChange={e => setLocation(e.target.value)}
              placeholder="e.g. Charlotte, NC 28277" required
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Email *</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com" required
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Password *</label>
            <input
              type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Min 8 chars, uppercase, lowercase, number, symbol" required minLength={8}
              style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              Min 8 characters: uppercase, lowercase, number, and symbol required.
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>Confirm Password *</label>
            <input
              type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password" required minLength={8}
              style={{ width: '100%', padding: '10px 14px', border: `1px solid ${confirmPassword && confirmPassword !== password ? '#f44336' : '#ddd'}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
            />
            {confirmPassword && confirmPassword !== password && (
              <div style={{ fontSize: '11px', color: '#f44336', marginTop: '4px' }}>Passwords do not match.</div>
            )}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '6px' }}>
              <span>Product Type *</span>
              <span
                title="📊 Catalog of Products and Services&#10;&#10;Total Products: 35&#10;Total Sectors: 12&#10;Live Databases: 20&#10;&#10;Sectors: IT, Financials, Healthcare, Industrials, Consumer Discretionary, Consumer Staples, Energy, Utilities, Real Estate, Materials, Communication Services, Manufacturing"
                style={{
                background: 'linear-gradient(135deg, #1565C0, #7B1FA2)',
                color: '#fff',
                borderRadius: '20px',
                padding: '4px 10px',
                fontSize: '10px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 6px rgba(21,101,192,0.3)',
                cursor: 'help',
              }}>
                📊 {PRODUCT_OPTIONS.length} Products · {SECTOR_ORDER.length} Sectors
              </span>
            </label>
            {/* Custom searchable sector dropdown */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="🔍 Search by sector, industry, or product name..."
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                onFocus={() => setProductSearch(productSearch || '')}
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }}
              />
              {/* Selected product display */}
              {productType && !productSearch && (
                <div style={{ fontSize: '12px', color: '#1565C0', marginTop: '4px', fontWeight: 500 }}>
                  ✅ Selected: {PRODUCT_OPTIONS.find(p => p.value === productType)?.emoji} {PRODUCT_OPTIONS.find(p => p.value === productType)?.label}
                </div>
              )}
              {/* Scrollable dropdown list */}
              <div style={{
                maxHeight: '280px',
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px',
                marginTop: '2px',
                background: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                {SECTOR_ORDER.map(sector => {
                  const sectorProducts = filteredProducts.filter(p => p.sector === sector);
                  // Sort: Type-1 alphabetically first, then Type-2+ alphabetically
                  const sorted = [...sectorProducts].sort((a, b) => {
                    const tierA = a.tier || 1;
                    const tierB = b.tier || 1;
                    if (tierA !== tierB) return tierA - tierB;
                    return a.label.localeCompare(b.label);
                  });
                  return (
                    <div key={sector}>
                      {/* Sector Header — bold, distinct background */}
                      <div style={{
                        padding: '8px 14px',
                        background: '#1a1a2e',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '12px',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}>
                        📊 {SECTOR_ORDER.indexOf(sector) + 1}. {sector} {sorted.length > 0 ? `(${sorted.length})` : ''}
                      </div>
                      {/* Products in this sector — grouped by tier with sub-headers */}
                      {sorted.length > 0 ? (() => {
                        const tiers = [...new Set(sorted.map(p => p.tier || 1))].sort();
                        return tiers.map(tier => {
                          const tierProducts = sorted.filter(p => (p.tier || 1) === tier);
                          if (tierProducts.length === 0) return null;
                          return (
                            <div key={tier}>
                              {/* Tier sub-header */}
                              <div title={tier === 1 ? 'CEO Smart Dashboard — Enterprise-wide KPIs, AI insights, and strategic directives at the C-suite level. Single executive view across all business units. LOB = Line of Business.' : tier === 2 ? 'Business Unit Smart Dashboard — CEO view + one Line of Business (LOB) drilldown. Combines executive-level intelligence with department-specific metrics, workflows, and AI recommendations for a single business unit.' : 'Operational Smart Dashboard — CEO view + one LOB + one Sub-Line of Business (Sub.LOB) drilldown. Full operational depth: executive strategy → business unit performance → granular team/process-level intelligence and automation.'} style={{
                                padding: '5px 14px 5px 16px',
                                background: tier === 1 ? '#E8F5E9' : tier === 2 ? '#F3E5F5' : '#E3F2FD',
                                fontSize: '11px',
                                fontWeight: 700,
                                color: tier === 1 ? '#2E7D32' : tier === 2 ? '#6A1B9A' : '#1565C0',
                                borderLeft: `3px solid ${tier === 1 ? '#4CAF50' : tier === 2 ? '#7B1FA2' : '#1976D2'}`,
                                cursor: 'help',
                              }}>
                                {tier === 1 ? '● Type-1 — CEO\'s Smart Dashboard' : tier === 2 ? '● Type-2 — (CEO + 1 LOB) Smart Dashboard' : `● Type-3 — (CEO + 1 LOB w 1 Sub.LOB) Smart Dashboard`}
                              </div>
                              {tierProducts.map(p => (
                                <div
                                  key={p.value}
                                  onClick={() => { if (p.dbReady) { setProductType(p.value); setProductSearch(''); } }}
                                  style={{
                                    padding: '8px 14px 8px 28px',
                                    cursor: p.dbReady ? 'pointer' : 'not-allowed',
                                    background: productType === p.value ? '#E3F2FD' : '#fff',
                                    borderLeft: productType === p.value ? '3px solid #1565C0' : '3px solid transparent',
                                    opacity: p.dbReady ? 1 : 0.45,
                                    fontSize: '13px',
                                    color: p.dbReady ? '#333' : '#999',
                                    transition: 'background 0.15s',
                                  }}
                                  onMouseEnter={e => { if (p.dbReady) e.currentTarget.style.background = productType === p.value ? '#E3F2FD' : '#f5f5f5'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = productType === p.value ? '#E3F2FD' : '#fff'; }}
                                >
                                  {p.emoji} {p.label} — <span style={{ color: '#888', fontSize: '11px' }}>{p.subSector}</span>
                                  {p.dbReady && <span style={{ marginLeft: '6px', fontSize: '10px', color: '#4CAF50' }}>✅</span>}
                                  {!p.dbReady && <span style={{ marginLeft: '6px', fontSize: '10px' }}>🔜</span>}
                                  {p.tier === 2 && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#7B1FA2', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>T2</span>}
                                  {p.isSubscription && <span style={{ marginLeft: '6px', fontSize: '10px', background: '#0D47A1', color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>SUB</span>}
                                </div>
                              ))}
                            </div>
                          );
                        });
                      })() : (
                        <div style={{ padding: '8px 14px 8px 24px', fontSize: '12px', color: '#bbb', fontStyle: 'italic' }}>
                          🔜 Coming Soon — No products available yet in this sector
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              ✅ = Live Database &nbsp;|&nbsp; 🔜 = Coming Soon &nbsp;|&nbsp; T2 = Enterprise ($1,000/mo) &nbsp;|&nbsp; SUB = Subscription
            </div>
            {productSearch && (
              <div style={{ fontSize: '11px', color: '#1565C0', marginTop: '2px' }}>
                Showing {filteredProducts.length} of {PRODUCT_OPTIONS.length} products matching "{productSearch}"
              </div>
            )}
          </div>

          {/* Locked defaults — shown as info, not editable */}
          <div style={{ background: '#F3E5F5', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', fontSize: '12px', color: '#4A148C' }}>
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>🔒 Auto-configured for you:</div>
            <div>Platform Role: <strong>Subscriber</strong></div>
            <div>Client Type: <strong>{PRODUCT_OPTIONS.find(p => p.value === productType)?.isSubscription ? 'Subscription' : 'Standalone'}</strong></div>
            {productType === 'China-US-Strategic-Intel.Type-1' ? (
              <>
                <div style={{ marginTop: '8px', marginBottom: '4px', fontWeight: 600 }}>Plan: <span style={{ fontWeight: 400, fontSize: '11px', color: '#7B1FA2' }}>(14-day free trial)</span></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '4px' }}>
                  <div style={{ background: '#fff', border: '1px solid #CE93D8', borderRadius: '6px', padding: '6px 8px', fontSize: '11px' }}>
                    <strong>Executive Pulse</strong> — $299/mo<br/><span style={{ color: '#666' }}>Individual executives</span>
                  </div>
                  <div style={{ background: '#fff', border: '2px solid #7B1FA2', borderRadius: '6px', padding: '6px 8px', fontSize: '11px' }}>
                    <strong>Portfolio Shield</strong> — $799/mo<br/><span style={{ color: '#7B1FA2', fontWeight: 600 }}>⭐ Best for Portfolio Managers</span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #CE93D8', borderRadius: '6px', padding: '6px 8px', fontSize: '11px' }}>
                    <strong>Enterprise Command</strong> — $2,499/mo<br/><span style={{ color: '#666' }}>C-Suite / Strategy Teams</span>
                  </div>
                  <div style={{ background: '#fff', border: '1px solid #CE93D8', borderRadius: '6px', padding: '6px 8px', fontSize: '11px' }}>
                    <strong>Institutional</strong> — Custom<br/><span style={{ color: '#666' }}>Banks / Hedge Funds (30-day trial)</span>
                  </div>
                </div>
              </>
            ) : (
              <div>Plan: <strong>{PRODUCT_OPTIONS.find(p => p.value === productType)?.tier === 2 ? 'Enterprise — $1,000/month' : 'Growth — $49/month'}</strong> (45-day free trial)</div>
            )}
            <div style={{ marginTop: '6px' }}>Database: <strong>{PRODUCT_DB_MAP[productType] ? `PostgreSQL (${PRODUCT_DB_MAP[productType].db})` : 'PostgreSQL (Free Tier)'}</strong></div>
          </div>

          <button
            type="submit" disabled={submitting}
            style={{
              width: '100%', padding: '12px', background: '#1a1a2e', color: '#fff', border: 'none',
              borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer',
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? 'Creating account...' : 'Sign Up →'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#999', marginTop: '16px' }}>
          Already have an account? <a href="/subscriber" style={{ color: '#1565C0' }}>Go to portal</a>
        </p>
        </>
        )}
      </div>

      {/* ── Product Intelligence Catalog — displayed in the purple area ──── */}
      <div style={{
        marginTop: '20px',
        background: 'rgba(255,255,255,0.12)',
        border: '1px solid rgba(255,255,255,0.2)',
        borderRadius: '16px',
        padding: '20px',
        maxWidth: '500px',
        width: '100%',
        backdropFilter: 'blur(10px)',
      }}>
        <div style={{ fontWeight: 700, fontSize: '14px', color: '#fff', marginBottom: '12px' }}>
          📈 Product Intelligence Catalog — 20 Products and Services across {SECTOR_ORDER.length} Sectors
        </div>

        {/* Sector breakdown grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginBottom: '14px', fontSize: '11px' }}>
          {SECTOR_ORDER.map(s => {
            const count = PRODUCT_OPTIONS.filter(p => p.sector === s).length;
            const liveCount = PRODUCT_OPTIONS.filter(p => p.sector === s && p.dbReady).length;
            return count > 0 ? (
              <div key={s} style={{ display: 'flex', justifyContent: 'space-between', color: '#e8e8e8', padding: '2px 0' }}>
                <span>{s}</span>
                <span style={{ color: liveCount > 0 ? '#69F0AE' : '#FFD54F', fontWeight: 600 }}>{liveCount}/{count}</span>
              </div>
            ) : null;
          })}
        </div>

        {/* BAI Goal, Objectives & Requirements */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '10px', marginBottom: '10px' }}>
          <div style={{ fontWeight: 700, color: '#80D8FF', fontSize: '12px', marginBottom: '6px' }}>🎯 Goal of Business Atomic Intelligence (BAI):</div>
          <div style={{ color: '#e0e0e0', fontSize: '11px', lineHeight: '1.5', marginBottom: '8px' }}>
            To provide strategies and automated strategy executions to improve YOUR business. Business users don't need to know anything about AI (Agentic AI, RAG AI, or GenAI). Minimal knowledge of ChatGPT, Gemini, or Claude is useful but not required.
          </div>
          <div style={{ fontWeight: 600, color: '#B2FF59', fontSize: '11px', marginBottom: '4px' }}>Objectives:</div>
          <div style={{ color: '#e0e0e0', fontSize: '10px', lineHeight: '1.5', paddingLeft: '8px', marginBottom: '8px' }}>
            ① Legal compliance at all times &nbsp;② Quality of Service &nbsp;③ Profit Optimization &nbsp;④ AI wraps around existing POS/transactions — no disruption to operations
          </div>
          <div style={{ fontWeight: 600, color: '#FFCC80', fontSize: '11px', marginBottom: '4px' }}>Requirements to Get Started:</div>
          <div style={{ color: '#e0e0e0', fontSize: '10px', lineHeight: '1.5', paddingLeft: '8px' }}>
            • Website URL with catalog of products/services (e.g. Restaurant: https://your-site.com)<br/>
            • CEO Directives + [Optional LOB/Sub-LOB preferences]<br/>
            • Read-only business transactions OR use BAI demo database<br/>
            • <span style={{ color: '#69F0AE' }}>No need to know RAG-AI, GenAI, or AgenticAI</span>
          </div>
        </div>

        {/* Most Popular */}
        <style>{`
          @keyframes wildfireWater {
            0% { content: '🔥'; opacity: 1; }
            25% { content: '💧'; opacity: 0.9; }
            50% { content: '🌊'; opacity: 1; }
            75% { content: '💨'; opacity: 0.7; }
            100% { content: '🔥'; opacity: 1; }
          }
          @keyframes waterDrop {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-3px); }
          }
          @keyframes wildlifeCycle {
            0% { content: '🐦'; }
            20% { content: '🦌'; }
            40% { content: '🐟'; }
            60% { content: '🦅'; }
            80% { content: '🐘'; }
            100% { content: '🐦'; }
          }
          @keyframes gentlePulse {
            0%, 100% { opacity: 0.8; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.15); }
          }
          @keyframes fireFlicker {
            0%, 100% { transform: translateY(0) scale(1); opacity: 1; }
            25% { transform: translateY(-2px) scale(1.1); opacity: 0.8; }
            50% { transform: translateY(-1px) scale(0.9); opacity: 1; }
            75% { transform: translateY(-3px) scale(1.05); opacity: 0.7; }
          }
          .fire-flicker {
            display: inline-block;
            animation: fireFlicker 1.2s ease-in-out infinite;
          }
          .wildfire-anim {
            display: inline-block;
            animation: waterDrop 1.5s ease-in-out infinite, gentlePulse 2s ease-in-out infinite;
          }
          .wildlife-anim {
            display: inline-block;
            animation: gentlePulse 2.5s ease-in-out infinite;
          }
        `}</style>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '10px', marginBottom: '10px' }}>
          <div style={{ fontWeight: 600, color: '#E1BEE7', fontSize: '12px', marginBottom: '4px' }}>🔥 Most Popular:</div>
          <div style={{ color: '#e0e0e0', fontSize: '11px' }}>
            Restaurant.Type-1 · <span className="wildfire-anim">💧</span>{' '}
            <span style={{ position: 'relative', display: 'inline-block', paddingTop: '14px' }}>
              <span style={{ position: 'absolute', top: '-2px', left: '0', width: '100%', display: 'flex', justifyContent: 'center', gap: '1px', fontSize: '9px' }}>
                <span className="fire-flicker" style={{ animationDelay: '0s' }}>🔥</span>
                <span className="fire-flicker" style={{ animationDelay: '0.3s' }}>🔥</span>
                <span className="fire-flicker" style={{ animationDelay: '0.6s' }}>🔥</span>
                <span className="fire-flicker" style={{ animationDelay: '0.9s' }}>🔥</span>
                <span className="fire-flicker" style={{ animationDelay: '0.15s' }}>🔥</span>
              </span>
              <span style={{ fontWeight: 600, color: '#FF8A65' }}>Wildfire</span>
            </span>{' '}Mitigation.Type-1 <span className="wildfire-anim">🌊</span> · IT-Consult.Type-500 · EV Charging Ops · BioPharma Executive
          </div>
          <div style={{ color: '#b0bec5', fontSize: '10px', marginTop: '4px', fontStyle: 'italic' }}>
            <span className="wildlife-anim">🐦</span><span className="wildlife-anim" style={{ animationDelay: '0.5s' }}>🦌</span><span className="wildlife-anim" style={{ animationDelay: '1s' }}>🐟</span>{' '}
            ChatGPT top-rated — the only AI product on the market for Environmental & Water Treatment dedicated to SAVING the EARTH: "IndustrialWater-PFAS.Type-2"{' '}
            <span className="wildlife-anim" style={{ animationDelay: '1.5s' }}>🌍</span><span className="wildlife-anim" style={{ animationDelay: '2s' }}>💧</span>
          </div>
        </div>

        {/* Market News — rotates daily from curated pool */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)', paddingTop: '10px' }}>
          <div style={{ fontWeight: 600, color: '#FFE082', fontSize: '12px', marginBottom: '6px' }}>📰 Market Intelligence ({new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}):</div>
          <div style={{ color: '#e0e0e0', fontSize: '11px', lineHeight: '1.6' }}>
            {(() => {
              const NEWS_POOL = [
                { icon: '⚡', tag: 'AI in Manufacturing', text: 'Market projected $155B by 2030 (35.3% CAGR). US alone reaching $52B by 2030.', src: 'MarketsandMarkets' },
                { icon: '🔌', tag: 'EV Charging', text: 'Infrastructure market growing to $93.6B in 2026 (+27.9% YoY). DC Fast Charging projected $285B by 2035.', src: 'TBRC, GMInsights' },
                { icon: '🌲', tag: 'AI in Utilities', text: '94% of utility CIOs increasing AI investment. Grid reliability AI market at 40% CAGR through 2029.', src: 'Startus Insights' },
                { icon: '☁️', tag: 'AI SaaS', text: 'Global AI SaaS market $30.3B in 2026, projected $367.6B by 2034 (36.6% CAGR).', src: 'Fortune Business Insights' },
                { icon: '🏗️', tag: 'Materials', text: 'Energy-intensive manufacturing margin intelligence — plant EBITDA scoring live for Steel/Cement/Chemicals/Aluminum.', src: 'PNP Platform' },
                { icon: '🏦', tag: 'Banking AI', text: 'AI in banking market expected to reach $64.03B by 2030, driven by fraud detection and regulatory compliance.', src: 'Grand View Research' },
                { icon: '🧬', tag: 'BioPharma AI', text: 'AI in drug discovery market projected $10.9B by 2030. 50% of pharma companies now using AI in R&D pipelines.', src: 'McKinsey' },
                { icon: '🏥', tag: 'Healthcare AI', text: 'Clinical AI market growing at 38.4% CAGR. Predictive diagnostics and operational intelligence leading adoption.', src: 'Precedence Research' },
                { icon: '🏭', tag: 'Predictive Maintenance', text: 'Industrial predictive maintenance market $28.2B by 2030. Unplanned downtime costs manufacturers $50B annually.', src: 'IoT Analytics' },
                { icon: '🌊', tag: 'Water Intelligence', text: 'PFAS treatment market reaching $4.2B by 2028. EPA regulations driving 300% increase in compliance monitoring.', src: 'Bluefield Research' },
                { icon: '🏠', tag: 'PropTech AI', text: 'AI in real estate market $1.3B by 2029. Automated valuations and tenant intelligence leading adoption.', src: 'Research and Markets' },
                { icon: '🛒', tag: 'Retail AI', text: 'AI in grocery/retail projected $24.1B by 2030. Inventory optimization and demand forecasting driving 35% efficiency gains.', src: 'Mordor Intelligence' },
                { icon: '🌏', tag: 'Geopolitical AI', text: 'Geopolitical risk analytics market growing 29% YoY. Corporate demand for supply chain intelligence surging post-2024.', src: 'Gartner' },
                { icon: '💧', tag: 'Industrial Water', text: 'Smart water management market $31.7B by 2030. IoT sensors + AI driving 25% reduction in water waste for manufacturers.', src: 'MarketsandMarkets' },
                { icon: '🔧', tag: 'Field Service AI', text: 'Field service management market $8.1B by 2028. AI-powered scheduling reducing truck rolls by 30%.', src: 'Verified Market Research' },
                { icon: '📋', tag: 'Process Intelligence', text: 'Process mining + AI market $12.6B by 2029. 60% of F500 companies adopting process intelligence platforms.', src: 'Gartner' },
                { icon: '🚚', tag: 'Logistics AI', text: 'AI in supply chain market $19.3B by 2028. Route optimization and demand sensing delivering 15% cost reduction.', src: 'Allied Market Research' },
                { icon: '⚡', tag: 'Grid Modernization', text: 'US utilities investing $120B in grid modernization through 2030. AI-driven outage prediction reducing SAIDI by 22%.', src: 'EEI, Morningstar' },
                { icon: '🏗️', tag: 'ESG Intelligence', text: 'ESG data and analytics market $2.6B by 2028. Carbon intensity monitoring now required by SEC for large filers.', src: 'Bloomberg' },
                { icon: '💻', tag: 'IT Consulting AI', text: 'AI augmenting 40% of IT consulting engagements by 2027. Resource utilization intelligence driving 20% margin improvement.', src: 'IDC' },
              ];
              const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
              const startIdx = (dayOfYear * 5) % NEWS_POOL.length;
              const todaysNews = [];
              for (let i = 0; i < 5; i++) todaysNews.push(NEWS_POOL[(startIdx + i) % NEWS_POOL.length]);
              return todaysNews.map((n, i) => (
                <div key={i} style={{ marginBottom: i < 4 ? '6px' : 0 }}>
                  <span style={{ color: '#80D8FF' }}>{n.icon} {n.tag}:</span> {n.text}
                  <span style={{ color: '#90a4ae', fontSize: '9px' }}> — {n.src}</span>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * SubscriberApp — Isolated subscriber portal.
 * Shows ONLY the signed-up client. Locked to their product type.
 * Menu: Onboard Client (if not onboarded), View Client, Update Client, Launch Application.
 * No List Clients, no Remove Client, no multi-client view.
 */
function SubscriberApp({ session, verified }) {
  const [subscriberSession, setSubscriberSessionState] = useState(session);
  const [activeModule, setActiveModule] = useState(
    session.onboarded
      ? 'view-client'
      : 'onboard'  // after email verification, go to Onboard Client wizard
  );
  const [clientConfig, setClientConfig] = useState(session.onboarded ? {
    client_id: session.client_id,
    company_name: session.company_name,
    product_name: session.product_name,
    product_type: session.product_type,
    tenant_type: session.tenant_type || 'INDEPENDENT',
    tenant_role: 'subscriber',
    plan: 'growth',
  } : null);
  const [isOnboarded, setIsOnboarded] = useState(session.onboarded || false);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentValidated, setPaymentValidated] = useState(session.payment_validated || false);
  const [waiverPending, setWaiverPending] = useState(false);

  // ── Email verified banner state ─────────────────────────────────────
  const [showVerifiedBanner, setShowVerifiedBanner] = useState(!!verified);
  useEffect(() => {
    if (showVerifiedBanner) {
      const timer = setTimeout(() => setShowVerifiedBanner(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showVerifiedBanner]);

  // ── Multi-business picker state ─────────────────────────────────────
  const [showBusinessPicker, setShowBusinessPicker] = useState(false);
  const [availableBusinesses, setAvailableBusinesses] = useState([]);
  const [businessSearchQuery, setBusinessSearchQuery] = useState('');

  // ── Auto-lookup existing businesses on mount ──────────────────────────
  // Always check for multiple businesses when the subscriber portal loads.
  // If multiple businesses exist, show a picker modal so the user can choose.
  // EXCEPTION: if the user just verified a new signup (verified=true), skip
  // the picker so the new product's onboarding flow is not hijacked by
  // existing businesses on the same email.
  const autoLookupDone = useRef(false);
  useEffect(() => {
    if (autoLookupDone.current) return;
    if (!session.email) return;
    autoLookupDone.current = true;

    // Skip auto-lookup when arriving from email verification — the user is
    // starting a brand-new signup for a specific product type. We store the
    // existing businesses so the "Switch Business" button still works, but
    // we do NOT show the picker automatically.
    if (verified) {
      const API_BASE = window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1';
      fetch(`${API_BASE}/subscribers/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: session.email }),
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data && data.businesses && data.businesses.length > 0) {
            // Store for the Switch Business button — but don't show picker
            setAvailableBusinesses(data.businesses);
          }
        })
        .catch(() => { /* non-blocking */ });
      return;
    }

    const API_BASE = window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1';
    fetch(`${API_BASE}/subscribers/check-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: session.email }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.businesses && data.businesses.length > 1) {
          // Multiple businesses — store them for the Switch Business button
          setAvailableBusinesses(data.businesses);
          // Always show picker when user has multiple products — let them choose
          setShowBusinessPicker(true);
        } else if (data && data.businesses && data.businesses.length === 1 && !session.onboarded) {
          // Single business, not yet loaded — auto-load it
          selectBusiness(data.businesses[0]);
        }
      })
      .catch(() => { /* non-blocking — fall through to onboarding wizard */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Select a business from the picker or auto-lookup ──────────────────
  const selectBusiness = useCallback((biz) => {
    // Respect actual payment_satisfied from the backend tenant record.
    // Businesses with status 'active' are fully provisioned and paid.
    // Only businesses with status 'provisioning' or missing payment_satisfied need payment.
    const isPaid = biz.payment_satisfied === true || biz.payment_satisfied === 'true'
      || biz.status === 'active';
    const updatedSession = {
      ...session,
      company_name: biz.company_name || session.company_name,
      location: biz.location || biz.business_address || session.location || '',
      business_address: biz.business_address || biz.location || session.business_address || '',
      website_url: biz.website_url || biz.website || session.website_url || '',
      product_type: biz.product_type || session.product_type,
      product_name: biz.product_name || biz.product_type || session.product_name,
      client_id: biz.client_id || session.client_id,
      plan: biz.plan || session.plan || 'growth',
      onboarded: !!biz.client_id,
      payment_validated: isPaid,
      payment_satisfied: isPaid,
      trial_start: biz.created_at || session.trial_start,
      created_at: biz.created_at || session.created_at,
    };
    setSubscriberSession(updatedSession);
    setSubscriberSessionState(updatedSession);
    setClientConfig({
      client_id: biz.client_id,
      company_name: biz.company_name,
      product_name: biz.product_name || biz.product_type,
      product_type: biz.product_type,
      tenant_type: 'INDEPENDENT',
      tenant_role: 'subscriber',
      plan: biz.plan || 'growth',
    });
    setIsOnboarded(!!biz.client_id);
    setPaymentValidated(isPaid);
    if (isPaid) {
      setPaymentSatisfied(true);
      setActiveModule('view-client');
    } else {
      setPaymentSatisfied(false);
      setActiveModule('onboard');
    }
    setShowBusinessPicker(false);
  }, [session]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Switch to a different business (from sidebar dropdown) ────────────
  const switchBusiness = useCallback((biz) => {
    selectBusiness(biz);
  }, [selectBusiness]);

  // Handle Stripe redirect back (?payment=success or ?payment=cancelled)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    if (paymentStatus === 'success') {
      const updatedSession = { ...session, payment_validated: true, trial_start: new Date().toISOString() };
      setSubscriberSession(updatedSession);
      setSubscriberSessionState(updatedSession);
      setPaymentValidated(true);
      setShowPayment(false);

      // Restore pending onboarding config saved before Stripe redirect
      try {
        const pendingConfig = JSON.parse(localStorage.getItem('pnp_pending_onboard_config') || 'null');
        if (pendingConfig) {
          localStorage.removeItem('pnp_pending_onboard_config');
          handleOnboardingComplete(pendingConfig);
        }
      } catch { /* ignore */ }

      // Clean URL
      window.history.replaceState({}, '', '/subscriber');
    } else if (paymentStatus === 'cancelled') {
      setShowPayment(true);
      window.history.replaceState({}, '', '/subscriber');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Client Portal state for launched app
  const [launchedApp, setLaunchedApp] = useState(null);
  const [portalActiveTab, setPortalActiveTab] = useState('overview');

  const handleOnboardingComplete = useCallback((config) => {
    const updatedSession = {
      ...subscriberSession,
      client_id: config.client_id || subscriberSession.client_id,
      onboarded: true,
    };
    setSubscriberSession(updatedSession);
    setSubscriberSessionState(updatedSession);
    setClientConfig(config);
    setIsOnboarded(true);
    setActiveModule('view-client');

    // Persist to localStorage for ListClients
    try {
      const existing = JSON.parse(localStorage.getItem('pnp_onboarded_clients') || '[]');
      const already = existing.find(c => c.client_id === config.client_id);
      if (!already) {
        const entry = {
          client_id: config.client_id,
          company_name: config.company_name,
          product_name: config.product_name || '',
          product_type: config.product_type || '',
          tenant_type: config.tenant_type || 'INDEPENDENT',
          tenant_role: 'subscriber',
          plan: 'growth',
          status: 'active',
          location: config.location || '',
          created_at: new Date().toISOString(),
        };
        localStorage.setItem('pnp_onboarded_clients', JSON.stringify([entry, ...existing]));
      }
      // ── Set Product-Type BUC defaults on onboarding/update-client ──
      const pt = (config.product_type || '').toLowerCase();
      const ver = `v3_${pt.split('.')[0] || 'generic'}`;
      localStorage.setItem(`pnp_daily_ver_${config.client_id}`, ver);
      ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_daily_${k}_${config.client_id}`));
      ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_analytics_${k}_${config.client_id}`));
    } catch { /* ignore */ }
  }, [subscriberSession]);

  const handleLaunchApp = useCallback((config) => {
    setLaunchedApp(config);
    setPortalActiveTab('overview');
  }, []);

  const handleBackFromPortal = useCallback(() => {
    setLaunchedApp(null);
    setPortalActiveTab('overview');
  }, []);

  // Coupon code state
  const [couponCode, setCouponCode] = useState('');
  const [couponMessage, setCouponMessage] = useState('');

  // ── Payment Gate: Restricted View when payment_satisfied is false ────
  // Check payment_satisfied from session (distinct from payment_validated which
  // is the in-session Stripe flow). payment_satisfied comes from the backend
  // tenant record and gates full dashboard access per Requirements 2.2, 2.5.
  // IMPORTANT: Never trust payment_validated from localStorage alone — always
  // verify against the backend. Initialize as false if client_id exists (will
  // be confirmed by the polling mechanism). Only trust it if no client_id yet
  // (fresh signup, nothing to check).
  const [paymentSatisfied, setPaymentSatisfied] = useState(
    !subscriberSession?.client_id ? true : (subscriberSession?.payment_satisfied === true)
  );
  // Allow restricted users to access onboarding without full dashboard
  const [restrictedBypass, setRestrictedBypass] = useState(false);

  // Poll /v1/subscribers/payment-status every 5 seconds when payment not satisfied
  useEffect(() => {
    if (paymentSatisfied) return;
    if (!subscriberSession?.client_id) return;

    const API_BASE = window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1';

    const pollPaymentStatus = async () => {
      try {
        const token = localStorage.getItem('pnp_access_token') || '';
        const res = await fetch(
          `${API_BASE}/subscribers/payment-status?client_id=${encodeURIComponent(subscriberSession.client_id)}`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );
        if (res.ok) {
          const data = await res.json();
          if (data.access === 'full') {
            setPaymentSatisfied(true);
            setPaymentValidated(true);
            setRestrictedBypass(false);
            // Update localStorage session with payment_satisfied
            const updatedSession = { ...subscriberSession, payment_satisfied: true, payment_validated: true };
            setSubscriberSession(updatedSession);
            setSubscriberSessionState(updatedSession);
          }
        }
      } catch (err) {
        console.error('[PNP] Payment status poll failed:', err);
      }
    };

    // Poll immediately then every 5 seconds
    pollPaymentStatus();
    const interval = setInterval(pollPaymentStatus, 5000);
    return () => clearInterval(interval);
  }, [paymentSatisfied, subscriberSession?.client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Also update paymentSatisfied when paymentValidated changes (e.g. after Stripe/coupon flow)
  useEffect(() => {
    if (paymentValidated && !paymentSatisfied) {
      setPaymentSatisfied(true);
      setRestrictedBypass(false);
    }
  }, [paymentValidated]); // eslint-disable-line react-hooks/exhaustive-deps
  const [couponApplying, setCouponApplying] = useState(false);

  // Payment validation — redirect to Stripe Checkout ($5 validation fee)
  const handlePaymentSuccess = async () => {
    const API_BASE = window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1';
    try {
      const session = subscriberSession;
      // Use clientConfig.client_id if available — it's set after the onboarding
      // wizard completes, before the payment screen appears. session.client_id
      // may still be empty at this point in the new verification flow.
      const effectiveClientId = (clientConfig && clientConfig.client_id) || session.client_id || '';
      const effectiveProductType = (clientConfig && clientConfig.product_type) || session.product_type || '';
      const effectiveProductName = (clientConfig && clientConfig.product_name) || session.product_name || '';
      const effectiveCompanyName = (clientConfig && clientConfig.company_name) || session.company_name || '';
      const res = await fetch(`${API_BASE}/subscribers/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: effectiveClientId,
          signup_id: effectiveClientId,
          email: session.email,
          product_type: effectiveProductType,
          product_name: effectiveProductName,
          company_name: effectiveCompanyName,
          coupon_code: couponCode.trim(),
          success_url: `${window.location.origin}/subscriber?payment=success`,
          cancel_url: `${window.location.origin}/subscriber?payment=cancelled`,
        }),
      });
      if (res.ok) {
        const data = await res.json();

        // ── Coupon path: coupon code accepted ───────────────────────────
        if (data.coupon_applied === true) {
          const updatedSession = {
            ...subscriberSession,
            payment_validated: true,
            trial_start: new Date().toISOString(),
            client_id: data.client_id || subscriberSession.client_id,
          };
          setSubscriberSession(updatedSession);
          setSubscriberSessionState(updatedSession);
          setPaymentValidated(true);
          setShowPayment(false);
          setCouponMessage('');

          // Restore pending onboarding config saved before the payment screen appeared
          // (same pattern as the Stripe ?payment=success redirect handler)
          try {
            const pendingConfig = JSON.parse(localStorage.getItem('pnp_pending_onboard_config') || 'null');
            if (pendingConfig) {
              localStorage.removeItem('pnp_pending_onboard_config');
              handleOnboardingComplete({
                ...pendingConfig,
                client_id: data.client_id || pendingConfig.client_id || subscriberSession.client_id,
              });
            }
          } catch { /* ignore */ }
          return;
        }

        // ── Waiver path: admin alias detected ───────────────────────────
        if (data.waiver === true) {
          setWaiverPending(true);
          setShowPayment(false);
          return;
        }

        if (data.checkout_url) {
          // Redirect to Stripe Checkout — real $5 charge, card saved for day-45 billing
          window.location.href = data.checkout_url;
          return;
        }
      } else {
        // Handle coupon error
        try {
          const errData = await res.json();
          if (errData.message && couponCode.trim()) {
            setCouponMessage(errData.message);
            return;
          }
        } catch { /* ignore */ }
      }
    } catch (e) { /* fall through to demo mode */ }

    // Demo/test mode fallback — no Stripe configured
    const updatedSession = { ...subscriberSession, payment_validated: true, trial_start: new Date().toISOString() };
    setSubscriberSession(updatedSession);
    setSubscriberSessionState(updatedSession);
    setPaymentValidated(true);
    setShowPayment(false);
  };

  // Apply coupon without Stripe
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage('Please enter a coupon code');
      return;
    }
    setCouponApplying(true);
    setCouponMessage('');
    await handlePaymentSuccess();
    setCouponApplying(false);
  };

  // ── Waiver polling: check approval status every 10 seconds ────────────
  const pollFailCountRef = useRef(0);
  const [waiverPollError, setWaiverPollError] = useState(false);

  useEffect(() => {
    if (!waiverPending) return;

    const API_BASE = window.location.pathname.startsWith('/pnp_staging') ? '/staging/v1' : '/v1';
    const clientId = subscriberSession.client_id;

    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/subscribers/${clientId}/waiver-status`);
        if (res.ok) {
          const data = await res.json();
          pollFailCountRef.current = 0;
          setWaiverPollError(false);

          if (data.payment_validated === true) {
            // Admin approved — transition to onboarding
            const updatedSession = { ...subscriberSession, payment_validated: true, trial_start: new Date().toISOString() };
            setSubscriberSession(updatedSession);
            setSubscriberSessionState(updatedSession);
            setPaymentValidated(true);
            setWaiverPending(false);
            setShowPayment(false);

            // Restore pending onboarding config if saved
            try {
              const pendingConfig = JSON.parse(localStorage.getItem('pnp_pending_onboard_config') || 'null');
              if (pendingConfig) {
                localStorage.removeItem('pnp_pending_onboard_config');
                handleOnboardingComplete(pendingConfig);
              }
            } catch { /* ignore */ }
          }
        } else {
          pollFailCountRef.current += 1;
          if (pollFailCountRef.current >= 3) setWaiverPollError(true);
        }
      } catch {
        pollFailCountRef.current += 1;
        if (pollFailCountRef.current >= 3) setWaiverPollError(true);
      }
    };

    // Poll immediately, then every 10 seconds
    poll();
    const interval = setInterval(poll, 10000);
    return () => clearInterval(interval);
  }, [waiverPending, subscriberSession, handleOnboardingComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Subscriber sidebar menu items
  // Rule: When payment is NOT satisfied, show restricted menu only:
  //   Onboard Client, Account, Validate Payment, Sign Out
  // When payment IS satisfied and onboarded, show full menu.
  const getSubscriberMenuItems = () => {
    const items = [];

    // Payment not satisfied — restricted menu only
    if (!paymentSatisfied) {
      items.push({ id: 'onboard', icon: '🚀', label: 'Onboard Client' });
      items.push({ id: 'account', icon: '👤', label: 'Account' });
      return items;
    }

    if (!isOnboarded) {
      // Fresh signup — show Onboard Client only
      items.push({ id: 'onboard', icon: '🚀', label: 'Onboard Client' });
    } else {
      // Fully onboarded AND paid — show full menu
      items.push({ id: 'view-client', icon: '🔍', label: 'View Client' });
      items.push({ id: 'update-client', icon: '✏️', label: 'Update Client' });
      items.push({ id: 'launch-app', icon: '▶️', label: 'Launch Application' });
      items.push({ id: 'view-config', icon: '⚙️', label: 'View Configuration' });
    }
    items.push({ id: 'account', icon: '👤', label: 'Account' });
    return items;
  };

  // ── Business Picker Modal (multi-business login) ────────────────────
  if (showBusinessPicker && availableBusinesses.length > 0) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{
          background: '#fff', borderRadius: '16px', padding: '36px',
          maxWidth: '560px', width: '90%', maxHeight: '80vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        }}>
          <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', fontSize: '20px' }}>📋 Select a Business</h2>
          <p style={{ color: '#666', margin: '0 0 6px', fontSize: '14px' }}>
            <strong>{session.email}</strong> has {availableBusinesses.length} registered business{availableBusinesses.length > 1 ? 'es' : ''}.
          </p>
          <p style={{ color: '#1565C0', margin: '0 0 20px', fontSize: '13px', fontWeight: 600 }}>
            Choose a business to continue, or register a new one.
          </p>

          <input
            type="text"
            placeholder="🔍 Search businesses..."
            value={businessSearchQuery}
            onChange={e => setBusinessSearchQuery(e.target.value)}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
              fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box',
              outline: 'none',
            }}
            onFocus={e => { e.target.style.borderColor = '#4f46e5'; }}
            onBlur={e => { e.target.style.borderColor = '#ddd'; }}
          />

          {availableBusinesses.filter(biz => {
            if (!businessSearchQuery.trim()) return true;
            const q = businessSearchQuery.toLowerCase();
            return (biz.company_name || '').toLowerCase().includes(q)
              || (biz.product_type || '').toLowerCase().includes(q)
              || (biz.product_name || '').toLowerCase().includes(q);
          }).map((biz, idx) => {
            const bizIsPaid = biz.payment_satisfied === true || biz.payment_satisfied === 'true' || biz.status === 'active';
            const needsPayment = !bizIsPaid && biz.status !== 'active';
            return (
            <div key={biz.client_id || idx} onClick={() => selectBusiness(biz)} style={{
              border: needsPayment ? '2px solid #FF9800' : '1px solid #e0e0e0', borderRadius: '10px', padding: '16px',
              marginBottom: '10px', cursor: 'pointer', transition: 'all 0.2s',
              background: needsPayment ? '#FFF8E1' : '#f9f9f9',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.background = '#f0f0ff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = needsPayment ? '#FF9800' : '#e0e0e0'; e.currentTarget.style.background = needsPayment ? '#FFF8E1' : '#f9f9f9'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ fontSize: '15px', color: '#1a1a2e' }}>{biz.company_name || 'Unnamed Business'}</strong>
                  <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                    {biz.product_name || biz.product_type || 'No product'} &nbsp;·&nbsp; {biz.plan || 'No plan'} &nbsp;·&nbsp;
                    <span style={{ color: biz.status === 'active' ? '#16a34a' : '#f59e0b', fontWeight: 600 }}>
                      {biz.status || 'unknown'}
                    </span>
                    {needsPayment && <span style={{ marginLeft: '8px', color: '#E65100', fontSize: '11px', fontWeight: 600 }}>⚠️ Payment pending</span>}
                  </div>
                </div>
                <span style={{ color: '#4f46e5', fontWeight: 600, fontSize: '13px' }}>Open →</span>
              </div>
            </div>
            );
          })}

          <div style={{ borderTop: '1px solid #eee', paddingTop: '16px', marginTop: '12px', display: 'flex', gap: '12px' }}>
            <button onClick={() => {
              setShowBusinessPicker(false);
              setActiveModule('onboard');
              setIsOnboarded(false);
              setClientConfig(null);
              // Reset payment state — each new business requires its own payment/coupon
              setPaymentValidated(false);
              setPaymentSatisfied(false);
              setShowPayment(false);
              const newSession = {
                ...subscriberSession,
                client_id: '',
                company_name: '',
                product_type: '',
                product_name: '',
                onboarded: false,
                payment_validated: false,
                trial_start: null,
              };
              setSubscriberSession(newSession);
              setSubscriberSessionState(newSession);
            }} style={{
              flex: 1, padding: '12px', borderRadius: '8px', border: '2px solid #4f46e5',
              background: '#fff', color: '#4f46e5', fontWeight: 600, cursor: 'pointer', fontSize: '14px',
            }}>
              + Register New Business
            </button>
            <button onClick={() => { clearSubscriberSession(); window.location.href = '/signup'; }} style={{
              padding: '12px 20px', borderRadius: '8px', border: '1px solid #ddd',
              background: '#f5f5f5', color: '#666', cursor: 'pointer', fontSize: '14px',
            }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Waiver waiting screen ─────────────────────────────────────────────
  if (waiverPending) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h2 style={{ color: '#1a1a2e', marginBottom: '8px' }}>Waiting for Admin Approval</h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
            An approval email has been sent to the platform administrator.
            Once approved, your 45-day free trial will begin automatically.
          </p>
          <div style={{ background: '#E8F5E9', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ fontWeight: 700, color: '#2E7D32', marginBottom: '4px' }}>📧 Approval Request Sent</div>
            <p style={{ fontSize: '13px', color: '#333', margin: 0 }}>
              The admin will review your account and click Approve.
              This page will update automatically — no need to refresh.
            </p>
          </div>
          {waiverPollError && (
            <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#E65100' }}>
              ⚠️ Having trouble checking status. Still trying...
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#999', fontSize: '13px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#4CAF50', animation: 'pulse 2s infinite' }}></span>
            Checking for approval...
          </div>
        </div>
      </div>
    );
  }

  // ── Restricted View: Only Onboard Client, Payment, Sign Out ──────────
  if (!paymentSatisfied && !restrictedBypass && subscriberSession?.client_id) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '520px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h2 style={{ color: '#1a1a2e', marginBottom: '8px' }}>Payment Required</h2>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
            Please complete payment to access your full dashboard. Until then, you can onboard a new client, complete payment, or sign out.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            <button
              onClick={() => { setRestrictedBypass(true); setActiveModule('onboard'); }}
              style={{
                padding: '14px 24px', background: '#1a1a2e', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              🚀 Onboard Client
            </button>
            <button
              onClick={() => { clearSubscriberSession(); window.location.href = '/signup'; }}
              style={{
                padding: '14px 24px', background: '#f5f5f5', color: '#666', border: '1px solid #ddd',
                borderRadius: '8px', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
              }}
            >
              🚪 Sign Out
            </button>
          </div>

          <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#E65100' }}>
            🔄 This page will automatically update once payment is confirmed.
          </div>
        </div>
      </div>
    );
  }

  // If launched app, show client portal
  if (launchedApp) {
    return (
      <div className="app client-portal-layout">
        <ClientPortalSidebar
          activeTab={portalActiveTab}
          setActiveTab={setPortalActiveTab}
          clientConfig={launchedApp}
          onBackToOperator={handleBackFromPortal}
        />
        <main className="main-content">
          <header className="header">
            <h1>PNP-Application — {launchedApp.company_name}</h1>
            <p className="subtitle">
              Subscriber Portal • {launchedApp.product_type} • Growth Plan
            </p>
          </header>
          <ClientApplication
            clientConfig={launchedApp}
            onBack={handleBackFromPortal}
            activeTab={portalActiveTab}
            setActiveTab={setPortalActiveTab}
          />
        </main>
      </div>
    );
  }

  // Payment validation modal
  if (showPayment) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f0c29, #302b63, #24243e)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', padding: '40px', maxWidth: '500px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <h2 style={{ textAlign: 'center', marginBottom: '8px' }}>💳 Account Setup — $5</h2>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '24px' }}>
            One-time $5 setup fee validates your card. Your subscription starts after 45 days.
          </p>

          <div style={{ background: '#E8F5E9', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ fontWeight: 700, color: '#2E7D32', marginBottom: '8px' }}>📋 Billing Timeline</div>
            <table style={{ width: '100%', fontSize: '13px' }}>
              <tbody>
                <tr><td style={{ padding: '4px 0', color: '#333' }}>Today</td><td style={{ fontWeight: 600 }}>$5 account setup (one-time)</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#333' }}>Days 1–45</td><td style={{ fontWeight: 600 }}>FREE — full access trial</td></tr>
                <tr><td style={{ padding: '4px 0', color: '#333' }}>Day 46+</td><td style={{ fontWeight: 600 }}>{(() => { const ptKey = Object.keys(PRODUCT_DB_MAP).find(k => PRODUCT_DB_MAP[k]?.displayName === subscriberSession?.product_type || k === subscriberSession?.product_type); const price = ptKey && PRODUCT_DB_MAP[ptKey]?.pricingMonthly ? PRODUCT_DB_MAP[ptKey].pricingMonthly : 49; return `$${price.toLocaleString()}/month (${price >= 100 ? 'Enterprise' : 'Growth'} Plan)`; })()}</td></tr>
              </tbody>
            </table>
          </div>

          {/* Coupon Code Section */}
          <div style={{ background: '#F3E5F5', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <div style={{ fontWeight: 700, color: '#6A1B9A', marginBottom: '8px' }}>🎟️ Have a coupon code?</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponMessage(''); }}
                style={{ flex: 1, padding: '10px 14px', border: '1px solid #CE93D8', borderRadius: '8px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px' }}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={couponApplying}
                style={{ padding: '10px 20px', background: '#7B1FA2', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                {couponApplying ? '...' : 'Apply'}
              </button>
            </div>
            {couponMessage && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: couponMessage.includes('Invalid') || couponMessage.includes('expired') ? '#C62828' : '#2E7D32' }}>
                {couponMessage}
              </div>
            )}
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#9C27B0' }}>
              Apply a valid coupon to skip the $5 fee and start your free trial instantly.
            </div>
          </div>

          <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '12px 16px', marginBottom: '24px', fontSize: '13px', color: '#E65100' }}>
            🔒 Secure payment via Stripe. Your card is saved for day-46 billing only. Cancel anytime before day 45 — no charge.
          </div>

          <button
            onClick={handlePaymentSuccess}
            style={{ width: '100%', padding: '14px', background: '#1a1a2e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}
          >
            Pay $5 & Start 45-Day Free Trial →
          </button>
          <button
            onClick={() => setShowPayment(false)}
            style={{ width: '100%', padding: '10px', background: 'transparent', color: '#999', border: 'none', fontSize: '13px', cursor: 'pointer', marginTop: '8px' }}
          >
            ← Back
          </button>
        </div>
      </div>
    );
  }

  // Subscriber operator console (isolated)
  const menuItems = getSubscriberMenuItems();

  const renderSubscriberModule = () => {
    switch (activeModule) {
      case 'onboard':
        return (
          <ClientOnboarding
            onComplete={(config) => {
              // Payment is handled inside the wizard at Step 7 (coupon/payment section).
              // onComplete fires after the user clicks Launch Application in the wizard,
              // which only appears after payment/coupon is validated.
              handleOnboardingComplete(config);
            }}
            onLaunchApp={handleLaunchApp}
            forceNewBusiness={true}
            subscriberMode={{
              locked: !!(subscriberSession.product_type),
              company_name: subscriberSession.company_name,
              email: subscriberSession.email,
              product_type: subscriberSession.product_type,
              product_name: subscriberSession.product_name,
              plan: 'growth',
              tenant_role: 'subscriber',
              tenant_type: 'standalone',
              payment_validated: false,  // always require fresh payment/coupon per new business
            }}
          />
        );
      case 'view-client':
        return <ViewClient clientConfig={clientConfig} onLaunchApp={handleLaunchApp} subscriberMode={true} />;
      case 'update-client':
        return <UpdateClient
          clientConfig={clientConfig || {
            client_id: subscriberSession.client_id || '',
            company_name: subscriberSession.company_name || '',
            product_type: subscriberSession.product_type || '',
            product_name: subscriberSession.product_name || '',
            contact_email: subscriberSession.email || '',
            plan: subscriberSession.plan || 'growth',
            tenant_type: subscriberSession.tenant_type || 'INDEPENDENT',
            tenant_role: 'subscriber',
            payment_validated: paymentValidated,
          }}
          subscriberMode={true}
          onComplete={() => setActiveModule('view-client')}
          onLaunchApp={handleLaunchApp}
        />;
      case 'launch-app':
        if (clientConfig) {
          // Directly launch the subscriber's own app — no client list shown
          return (
            <ClientApplication
              clientConfig={clientConfig}
              onBack={() => setActiveModule('view-client')}
              activeTab={portalActiveTab}
              setActiveTab={setPortalActiveTab}
            />
          );
        }
        return <div style={{ padding: '24px', color: '#666' }}>No client configured yet. Please complete onboarding first.</div>;
      case 'view-config':
        if (clientConfig) {
          return <ViewConfiguration clientConfig={clientConfig} />;
        }
        return <div style={{ padding: '24px', color: '#666' }}>No client configured yet. Please complete onboarding first.</div>;
      case 'account':
        return (
          <AccountPage
            session={subscriberSession}
            onSessionUpdate={(updated) => {
              setSubscriberSession(updated);
              setSubscriberSessionState(updated);
            }}
          />
        );
      default:
        return <ViewClient clientConfig={clientConfig} onLaunchApp={handleLaunchApp} subscriberMode={true} />;
    }
  };

  return (
    <div className="app">
      {/* Subscriber Sidebar */}
      <aside className="sidebar">
        <div className="logo">
          <h2>PNP Infra</h2>
          <span style={{
            display: 'inline-block', background: '#1565C0', color: '#fff',
            padding: '2px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 700,
            marginTop: '4px', letterSpacing: '0.5px',
          }}>SUBSCRIBER</span>
        </div>

        <div style={{ padding: '12px 16px', background: '#f0f7ff', borderRadius: '8px', margin: '8px 12px', fontSize: '12px' }}>
          <div style={{ fontWeight: 700, color: '#1a1a2e', marginBottom: '4px' }}>{subscriberSession.company_name}</div>
          <div style={{ color: '#666' }}>{subscriberSession.email}</div>
          <div style={{ color: '#1565C0', marginTop: '4px', fontWeight: 600 }}>{subscriberSession.product_name}</div>
          <div style={{ color: '#2E7D32', fontSize: '11px', marginTop: '2px' }}>{(() => { const ptKey = Object.keys(PRODUCT_DB_MAP).find(k => PRODUCT_DB_MAP[k]?.displayName === subscriberSession.product_type || k === subscriberSession.product_type); const price = ptKey && PRODUCT_DB_MAP[ptKey]?.pricingMonthly ? PRODUCT_DB_MAP[ptKey].pricingMonthly : 49; return price >= 100 ? `Enterprise Plan • $${price.toLocaleString()}/mo` : `Growth Plan • $${price}/mo`; })()}</div>
          {availableBusinesses.length > 1 && activeModule !== 'onboard' && (
            <button
              onClick={() => setShowBusinessPicker(true)}
              style={{
                marginTop: '8px', width: '100%', padding: '6px', background: '#e8eaf6',
                border: '1px solid #c5cae9', borderRadius: '6px', fontSize: '11px',
                color: '#3949ab', fontWeight: 600, cursor: 'pointer',
              }}
            >
              🔄 Switch Business ({availableBusinesses.length})
            </button>
          )}
        </div>

        <nav className="nav">
          {menuItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeModule === item.id ? 'active' : ''}`}
              onClick={() => setActiveModule(item.id)}
              aria-current={activeModule === item.id ? 'page' : undefined}
            >
              <span className="icon">{item.icon}</span>
              <span className="label">{item.label}</span>
            </button>
          ))}
        </nav>

        {!paymentValidated && (
          <div style={{ padding: '12px 16px', margin: '8px 12px' }}>
            <button
              onClick={() => setShowPayment(true)}
              style={{
                width: '100%', padding: '8px', background: '#FF9800', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer',
              }}
            >
              💳 Validate Payment
            </button>
          </div>
        )}

        <div style={{ padding: '12px 16px', margin: '8px 12px', borderTop: '1px solid #eee', fontSize: '11px' }}>
          <button
            onClick={() => { clearSubscriberSession(); window.location.href = '/signup'; }}
            style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '11px', padding: 0 }}
          >
            🚪 Sign Out
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="header">
          <h1>Plug-N-Play-Infra.ai: Business Intelligence Platform</h1>
          <p className="subtitle">
            {subscriberSession.company_name} — {subscriberSession.product_name} • Subscriber Portal
          </p>
        </header>
        {/* ── Email verified success banner ── */}
        {showVerifiedBanner && (
          <div style={{ background: '#E8F5E9', color: '#2E7D32', padding: '12px 16px', borderRadius: '8px', fontSize: '14px', margin: '0 0 16px', border: '1px solid #A5D6A7', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>✅ Email verified! Your account is now active.</span>
            <button onClick={() => setShowVerifiedBanner(false)} style={{ background: 'none', border: 'none', color: '#2E7D32', cursor: 'pointer', fontSize: '16px', padding: '0 4px' }}>×</button>
          </div>
        )}
        {renderSubscriberModule()}
      </main>
    </div>
  );
}

export default App;
